/**
 * Theme. Plan §3.1: the user menu offers Light / Dark / Auto, Auto follows the
 * OS, and "nothing moves between modes" — only the token values change.
 *
 * The choice is per device (plan §3.4), so it lives in localStorage rather than
 * on the user record.
 *
 * **Light until somebody picks otherwise** (Aaron, step 27). The default was
 * Auto, so anybody whose computer runs dark met a dark sign-up, a dark
 * set-password page and a dark app before they had chosen anything. Auto is
 * still on the menu; it is simply no longer what nobody chose.
 *
 * **Only a choice is stored.** The old key was written on every page load with
 * whatever was being applied, so "auto" in it meant "the default at the time"
 * as often as "somebody picked Auto" — and the two cannot be told apart now.
 * So a choice goes under a new key, written only by `chooseTheme`, and the old
 * key is read once on the way: light or dark there was somebody picking (the
 * default never wrote either), and auto there is treated as no choice at all.
 */
export type Theme = 'light' | 'dark' | 'auto'

/** What somebody who has never picked gets. */
export const DEFAULT_THEME: Theme = 'light'

const STORAGE_KEY = 'rowted.theme.choice'
/** Written on every load until step 27; read, never written. */
const LEGACY_KEY = 'rowted.theme'

const isTheme = (value: string | null): value is Theme =>
  value === 'light' || value === 'dark' || value === 'auto'

export function readTheme(): Theme {
  try {
    const chosen = localStorage.getItem(STORAGE_KEY)
    if (isTheme(chosen)) return chosen
    const legacy = localStorage.getItem(LEGACY_KEY)
    if (legacy === 'light' || legacy === 'dark') return legacy
  } catch {
    // A private window or blocked storage: the default, rather than a crash.
  }
  return DEFAULT_THEME
}

/** Puts a theme on the page. Stores nothing — see `chooseTheme`. */
export function applyTheme(theme: Theme): void {
  if (theme === 'auto') {
    // No attribute: the media query in tokens.css decides.
    document.documentElement.removeAttribute('data-theme')
  } else {
    document.documentElement.setAttribute('data-theme', theme)
  }
  syncThemeColor()
  followSystemTheme()
}

/**
 * The installed app's title bar (Windows) and status bar (Android) are the
 * page's `theme-color`, and they are the top bar's `surface` in whichever theme
 * is on — so the app runs on into the edge of the window rather than stopping
 * at a band of another colour. Read off the page, like `tokenColour()`, so it
 * is the token and never a second copy of it.
 */
export function syncThemeColor(): void {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
  if (!meta) return
  const rgb = getComputedStyle(document.documentElement)
    .getPropertyValue('--color-surface').trim().split(/\s+/).map(Number)
  if (rgb.length !== 3 || rgb.some((n) => !Number.isInteger(n) || n < 0 || n > 255)) return
  meta.content = `#${rgb.map((n) => n.toString(16).padStart(2, '0')).join('')}`
}

let followingSystem = false

/** Auto follows the computer, so the title bar has to follow it too, live. */
function followSystemTheme(): void {
  if (followingSystem || typeof window.matchMedia !== 'function') return
  followingSystem = true
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener?.('change', syncThemeColor)
}

/** Somebody picked one from the user menu: remember it on this device and apply it. */
export function chooseTheme(theme: Theme): void {
  try {
    localStorage.setItem(STORAGE_KEY, theme)
  } catch {
    // Not remembered, still applied.
  }
  applyTheme(theme)
}
